/* eslint-disable max-lines */
//
import { defaultsDeep } from 'lodash';
import createReducer from '../../reducers/utils/createReducer';
import * as c from './types';
import * as conf from '../../constants/config';

export type FeatureFlagType = {
  enabled: boolean;
  machines: { id: number; enabled: boolean }[];
};

export interface ConfigData {
  background: string;
  logo: string;
  backgroundColor: string;
  isEquity: boolean;
  showVotings: boolean;
  showDSO: boolean;
  showDividends: boolean;
  showInnerTrading: boolean;
  primaryColor?: string;
  realtime: {
    noOfCardsPerPage: number;
    pageChangeInterval: number;
    singleMachineModeTvVersionEnabled: boolean;
    multiMachineModeTvVersionEnabled: boolean;
    metrics?: any;
    realtimeNonKpi?: any;
  };
  machineMetricConfig: any;
  legacyTvMode: { rows: number; columns: number };
  kpisTvMode: { rows: number; columns: number };
  work: {
    [key: string]: string;
  };
  production: Record<string, unknown>;
  isSidebarCollapsed: boolean;
  featureFlags: Record<string, FeatureFlagType> | undefined;
  shiftSettings:
    | {
        allowEditionUpToDays?: number;
        moveNightToNextDay?: boolean;
      }
    | undefined;
}
const defaultConfig: ConfigData = {
  background: conf.LOGIN_BACKGROUND,
  logo: conf.LOGO,
  backgroundColor: conf.LOGIN_BACKGROUND_COLOR,
  isEquity: false,
  showVotings: false,
  showDSO: false,
  showDividends: false,
  showInnerTrading: false,
  realtime: {
    singleMachineModeTvVersionEnabled: false,
    multiMachineModeTvVersionEnabled: false,
    noOfCardsPerPage: 4,
    pageChangeInterval: 10,
    metrics: {}
  },
  machineMetricConfig: {},
  legacyTvMode: {
    rows: 0,
    columns: 0
  },
  kpisTvMode: {
    rows: 0,
    columns: 0
  },
  isSidebarCollapsed: false,
  work: {},
  production: {},
  featureFlags: undefined,
  shiftSettings: undefined
};
type ConfigStateType = {
  data: ConfigData;
  error: any;
  isLoading: boolean;
  isUpdateInProgress: boolean;
};

const initialState: ConfigStateType = {
  data: { ...defaultConfig },
  error: null,
  isLoading: false,
  isUpdateInProgress: false
};

const fallbackColor = '#e21b4f';

function setCssVariable(name: string, value: string): void {
  if (document.documentElement) {
    document.documentElement.style.setProperty(name, value);
  }
}

const configReducer = createReducer(initialState, {
  [c.GET_CONFIG_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isLoading: true
  }),
  [c.GET_CONFIG_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    if (payload.primaryColor) {
      setCssVariable('--primary', payload.primaryColor);
      setCssVariable('--primary-dark', payload.primaryColor);
      setCssVariable('--primary-light', payload.primaryColor);
      // setCssVariable('--blue', payload.primaryColor);
    } else {
      setCssVariable('--primary', fallbackColor);
      setCssVariable('--primary-dark', fallbackColor);
      setCssVariable('--primary-light', fallbackColor);
    }
    return {
      ...state,
      data: {
        ...defaultsDeep(payload, defaultConfig),
        background: payload.background || defaultConfig.background,
        logo: payload.logo || defaultConfig.logo,
        backgroundColor:
          payload.backgroundColor || defaultConfig.backgroundColor,
        primaryColor: payload.primaryColor || fallbackColor,
        isEquity: Boolean(Number(payload.isEquity))
      },
      isLoading: false
    };
  },
  [c.GET_CONFIG_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      data: {
        ...defaultConfig
      },
      error: payload,
      isLoading: false,
      isUpdateInProgress: false
    };
  },
  [c.GET_COMPANY_UI_CONFIG_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isLoading: true
  }),
  [c.GET_COMPANY_UI_CONFIG_SUCCESS]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      data: {
        ...state.data,
        ...payload
      },
      isLoading: false
    };
  },
  [c.GET_COMPANY_UI_CONFIG_ERROR]: (state = initialState, { payload }) => {
    if (!payload) return state;
    return {
      ...state,
      error: payload,
      isLoading: false
    };
  },
  [c.UPDATE_COMPANY_UI_CONFIG_REQUEST]: (state = initialState) => ({
    ...state,
    error: null,
    isUpdateInProgress: true
  }),
  [c.UPDATE_COMPANY_UI_CONFIG_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: any }
  ): ConfigStateType => ({
    ...state,
    data: {
      ...state.data,
      ...action.payload
    },
    error: null,
    isLoading: false,
    isUpdateInProgress: false
  }),
  [c.UPDATE_COMPANY_UI_CONFIG_ERROR]: (state = initialState, { payload }) => ({
    ...state,
    error: payload,
    isUpdateInProgress: false
  }),
  [c.UPDATE_MACHINE_METRICS_CONFIG]: (state = initialState, { payload }) => {
    if (!state) return state;
    const clonedMachineMetricConfig = { ...state.data.machineMetricConfig };
    const machineIds = Object.keys(payload);
    for (const machineId of machineIds) {
      if (
        Object.prototype.hasOwnProperty.call(
          clonedMachineMetricConfig,
          machineId
        )
      ) {
        clonedMachineMetricConfig[machineId] = payload[machineId];
      }
    }
    return {
      ...state,
      data: {
        ...state.data,
        machineMetricConfig: clonedMachineMetricConfig
      }
    };
  },
  [c.TOGGLE_SIDEBAR_COLLAPSED]: (state = initialState) => ({
    ...state,
    data: {
      ...state.data,
      isSidebarCollapsed: !state.data.isSidebarCollapsed
    }
  })
});

export default configReducer;
